/**
 * Single source of truth for physical engine / generator / stabilizer
 * placement on a yacht. Used by both the Yacht editor (the location
 * dropdown) and the Logbook (label rendering when capturing engine hours
 * after a trip).
 *
 * Keep this list small and stable — anything else gets stored as the raw
 * string on the unit's ``location`` field, and the UI silently surfaces
 * unknown values verbatim instead of crashing.
 */
export const ENGINE_LOCATIONS = [
  "Port Outer",
  "Port Center",
  "Port Inner",
  "Port",
  "Center",
  "Starboard",
  "Starboard Inner",
  "Starboard Center",
  "Starboard Outer",
];

/** Friendly label for a unit's ``location`` value. Unknown values pass
 *  through unchanged so legacy data isn't silently dropped. */
export function labelForLocation(value) {
  return value || "";
}

/**
 * Default positional layout when an engine/generator has no explicit
 * ``location`` set yet. Mirrors how multi-engine yachts are typically
 * arranged on the hull so the Logbook never has to fall back to a
 * generic "Engine 1" / "Engine 2" label.
 *
 *   1 unit  → ["Center"]
 *   2 units → ["Port", "Starboard"]
 *   3 units → ["Port", "Center", "Starboard"]
 *   4 units → ["Port Outer", "Port Inner", "Starboard Inner", "Starboard Outer"]
 *
 * Anything beyond 4 falls back to indexed Port/Starboard pairs.
 * Captains can always override by setting the explicit `location` field
 * in the Yacht editor.
 */
export function defaultLocationLayout(count) {
  if (count <= 0) return [];
  if (count === 1) return ["Center"];
  if (count === 2) return ["Port", "Starboard"];
  if (count === 3) return ["Port", "Center", "Starboard"];
  if (count === 4) return ["Port Outer", "Port Inner", "Starboard Inner", "Starboard Outer"];
  // 5+ units → pair them by side, leaving the middle index as Center
  const out = [];
  for (let i = 0; i < count; i += 1) {
    if (i * 2 + 1 === count) {
      out.push("Center");
    } else if (i < count / 2) {
      out.push(`Port ${i + 1}`);
    } else {
      out.push(`Starboard ${count - i}`);
    }
  }
  return out;
}

/**
 * Resolve the displayable location label for a unit at `index` inside a
 * list of length `total`. Explicit `unit.location` always wins; otherwise
 * the positional default from {@link defaultLocationLayout} is used.
 * Returns an empty string only when both inputs are unusable (count 0).
 */
export function resolveUnitLocation(unit, index, total) {
  if (unit?.location && unit.location.trim()) return unit.location.trim();
  const layout = defaultLocationLayout(total);
  return layout[index] || "";
}

/**
 * Format the display name shown in the Logbook trip form + entries.
 * Strict requirement (per user request 2026-05-31): NO brand / type /
 * serial — only the position. Falls back to the positional default when
 * no explicit location is set so the captain never sees "Engine 1".
 */
export function engineDisplayName(unit, index = 0, total = 1) {
  return resolveUnitLocation(unit, index, total) || "Center";
}
